import { useEffect, useMemo, useState } from 'react';
import { useOp } from '../../api.ts';
import { runWrite } from '../../ui/changes.ts';
import { Empty, ErrorBox, Loading } from '../../ui/kit.tsx';
import { useToast } from '../../ui/toasts.tsx';
import type { NavItem, SiteDetail } from './types.ts';

/**
 * The site's main navigation (docs/navigation.md): switches for Home, Stores A–Z and All categories, and an ordered
 * list of menus and links. Store, category and campaign menus show what is picked here, in this order; nothing
 * picked = automatic. Saved on its own, as one setting of the site.
 */

interface NavOptions {
  stores: { slug: string; name: string; live: number }[];
  categories: { slug: string; name: string }[];
  campaigns: { slug: string; name: string; startsAt: string; endsAt: string }[];
}

const DEFAULT_ITEMS: NavItem[] = [
  { type: 'shops', shops: [], limit: 12 },
  { type: 'categories', categories: [], limit: 12 },
  { type: 'campaigns', campaigns: [] },
  { type: 'page', page: 'how_we_verify' },
];

const SWITCHES: { type: 'home' | 'stores' | 'all_categories'; label: string; hint: string }[] = [
  { type: 'home', label: 'Show Home link', hint: 'The logo links home already.' },
  { type: 'stores', label: 'Show Stores link', hint: 'Link to the stores A–Z page.' },
  { type: 'all_categories', label: 'Show Categories', hint: 'A menu with every category.' },
];

const TYPE_LABELS: Record<NavItem['type'], string> = {
  home: 'Home link',
  stores: 'Stores A–Z link',
  all_categories: 'All categories (menu)',
  shops: 'Top stores (menu)',
  categories: 'Categories (menu)',
  campaigns: 'Campaigns',
  page: 'Site page',
  link: 'Custom link',
};

const PAGES = [
  { value: 'how_we_verify', label: 'How we test codes' },
  { value: 'about', label: 'About us' },
  { value: 'affiliate_disclosure', label: 'How we make money' },
  { value: 'privacy', label: 'Privacy and cookies' },
  { value: 'legal', label: 'Legal notice' },
] as const;

export function NavigationPanel({ site }: { site: SiteDetail }) {
  const toast = useToast();
  const options = useOp<NavOptions>('admin.sites.navigationOptions', { ref: site.ref });
  const initial = useMemo(() => site.settings.navigation?.items ?? DEFAULT_ITEMS, [site]);
  const [items, setItems] = useState<NavItem[]>(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => setItems(initial), [initial]);
  const disabled = !site.canAdmin;
  const isDefault = !site.settings.navigation;

  const has = (type: NavItem['type']) => items.some((item) => item.type === type);
  const toggle = (type: 'home' | 'stores' | 'all_categories', on: boolean) =>
    setItems((list) => (on ? (type === 'home' ? [{ type }, ...list] : [...list, { type }]) : list.filter((item) => item.type !== type)));
  const update = (index: number, change: Partial<NavItem>) => setItems((list) => list.map((item, i) => (i === index ? ({ ...item, ...change } as NavItem) : item)));
  const move = (index: number, by: number) =>
    setItems((list) => {
      const next = [...list];
      const target = index + by;
      if (target < 0 || target >= next.length) return list;
      [next[index], next[target]] = [next[target]!, next[index]!];
      return next;
    });
  const remove = (index: number) => setItems((list) => list.filter((_, i) => i !== index));
  const add = (type: NavItem['type']) =>
    setItems((list) => [
      ...list,
      type === 'shops'
        ? { type, shops: [], limit: 12 }
        : type === 'categories'
          ? { type, categories: [], limit: 12 }
          : type === 'campaigns'
            ? { type, campaigns: [] }
            : type === 'page'
              ? { type, page: 'about' }
              : type === 'link'
                ? { type, label: 'New link', href: '/' }
                : { type },
    ]);

  const save = async (navigation: { items: NavItem[] } | null) => {
    setSaving(true);
    await runWrite(toast, 'admin.sites.update', { ref: site.ref, settings: { navigation } }, navigation ? 'Navigation saved' : 'Default navigation restored');
    setSaving(false);
  };

  return (
    <div className="nav-editor">
      <p className="muted small">
        The main menu under the site header, in this order. Menus show what you pick here, in the order you pick it; nothing picked means automatic (most popular stores with live coupons, categories with the most stores, the running and next campaigns).
        {isDefault ? ' This site uses the default navigation.' : ''}
      </p>
      {options.error ? <ErrorBox error={options.error} onRetry={options.reload} /> : null}
      {options.loading && !options.data ? <Loading /> : null}

      <fieldset disabled={disabled}>
        <h3 className="section-title">Options</h3>
        <div className="stack">
          {SWITCHES.map((s) => (
            <label key={s.type} className="inline-check">
              <input type="checkbox" checked={has(s.type)} onChange={(e) => toggle(s.type, e.target.checked)} /> {s.label} <span className="muted small">{s.hint}</span>
            </label>
          ))}
        </div>

        <h3 className="section-title">Items</h3>
        {items.length === 0 ? <Empty>No items: the site shows no main menu.</Empty> : null}
        <ol className="nav-items">
          {items.map((item, index) => (
            <li key={`${item.type}-${index}`} className="nav-item card">
              <div className="nav-item-head">
                <strong>{TYPE_LABELS[item.type]}</strong>
                <span className="nav-item-actions">
                  <button type="button" className="btn btn-small" onClick={() => move(index, -1)} disabled={index === 0} aria-label="Move up">
                    ↑
                  </button>
                  <button type="button" className="btn btn-small" onClick={() => move(index, 1)} disabled={index === items.length - 1} aria-label="Move down">
                    ↓
                  </button>
                  <button type="button" className="btn btn-small" onClick={() => remove(index)} aria-label="Remove">
                    Remove
                  </button>
                </span>
              </div>
              <ItemFields item={item} options={options.data} onChange={(change) => update(index, change)} />
            </li>
          ))}
        </ol>

        <div className="inline-form">
          <label>
            Add
            <select value="" onChange={(e) => e.target.value && add(e.target.value as NavItem['type'])}>
              <option value="">Choose…</option>
              <option value="shops">Top stores (menu)</option>
              <option value="categories">Categories (menu)</option>
              <option value="campaigns">Campaigns</option>
              <option value="page">Site page</option>
              <option value="link">Custom link</option>
            </select>
          </label>
        </div>

        <div className="form-actions">
          <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void save({ items })}>
            Save navigation
          </button>
          {!isDefault ? (
            <button type="button" className="btn" disabled={saving} onClick={() => void save(null)}>
              Use the default navigation
            </button>
          ) : null}
        </div>
      </fieldset>
    </div>
  );
}

function ItemFields({ item, options, onChange }: { item: NavItem; options: NavOptions | null; onChange: (change: Partial<NavItem>) => void }) {
  const labelField = (placeholder: string) => (
    <label>
      Label
      <input value={'label' in item ? (item.label ?? '') : ''} placeholder={placeholder} maxLength={40} onChange={(e) => onChange({ label: e.target.value || undefined } as Partial<NavItem>)} />
    </label>
  );
  switch (item.type) {
    case 'home':
      return labelField('Home');
    case 'stores':
      return labelField('Stores A–Z');
    case 'all_categories':
      return labelField('Categories');
    case 'shops':
      return (
        <div className="nav-item-fields">
          {labelField('Top stores')}
          <label>
            Show at most
            <input type="number" min={1} max={40} value={item.limit ?? 12} onChange={(e) => onChange({ limit: Number(e.target.value) } as Partial<NavItem>)} />
          </label>
          <Picker
            label="Stores in the menu"
            hint="Empty = the most popular stores with live coupons."
            chosen={item.shops ?? []}
            all={(options?.stores ?? []).map((s) => ({ value: s.slug, label: `${s.name}${s.live ? ` (${s.live} live)` : ''}` }))}
            onChange={(shops) => onChange({ shops } as Partial<NavItem>)}
          />
        </div>
      );
    case 'categories':
      return (
        <div className="nav-item-fields">
          {labelField('Categories')}
          <label>
            Show at most
            <input type="number" min={1} max={40} value={item.limit ?? 12} onChange={(e) => onChange({ limit: Number(e.target.value) } as Partial<NavItem>)} />
          </label>
          <Picker
            label="Categories in the menu"
            hint="Empty = the categories with the most stores."
            chosen={item.categories ?? []}
            all={(options?.categories ?? []).map((c) => ({ value: c.slug, label: c.name }))}
            onChange={(categories) => onChange({ categories } as Partial<NavItem>)}
          />
        </div>
      );
    case 'campaigns':
      return (
        <div className="nav-item-fields">
          {labelField('Campaigns')}
          <Picker
            label="Campaign pages"
            hint="One or two show as links side by side; three or more as one menu with this label. Empty = running now or starting within 60 days. Ended campaigns drop out on their own."
            chosen={item.campaigns ?? []}
            all={(options?.campaigns ?? []).map((c) => ({ value: c.slug, label: `${c.name} (${c.startsAt.slice(0, 10)} – ${c.endsAt.slice(0, 10)})` }))}
            onChange={(campaigns) => onChange({ campaigns } as Partial<NavItem>)}
          />
        </div>
      );
    case 'page':
      return (
        <div className="nav-item-fields">
          <label>
            Page
            <select value={item.page} onChange={(e) => onChange({ page: e.target.value } as Partial<NavItem>)}>
              {PAGES.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
          {labelField(PAGES.find((p) => p.value === item.page)?.label ?? '')}
        </div>
      );
    case 'link':
      return (
        <div className="nav-item-fields">
          <label>
            Label
            <input required value={item.label} maxLength={40} onChange={(e) => onChange({ label: e.target.value } as Partial<NavItem>)} />
          </label>
          <label>
            Link
            <input required value={item.href} placeholder="/black-friday or https://…" onChange={(e) => onChange({ href: e.target.value } as Partial<NavItem>)} />
          </label>
        </div>
      );
  }
}

/** Ordered pick list: add from the select, reorder with arrows, remove with ×. */
function Picker({ label, hint, chosen, all, onChange }: { label: string; hint: string; chosen: string[]; all: { value: string; label: string }[]; onChange: (values: string[]) => void }) {
  const labelOf = new Map(all.map((o) => [o.value, o.label]));
  const remaining = all.filter((o) => !chosen.includes(o.value));
  const move = (index: number, by: number) => {
    const next = [...chosen];
    const target = index + by;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target]!, next[index]!];
    onChange(next);
  };
  return (
    <div className="picker">
      <span className="picker-label">{label}</span>
      <span className="muted small">{hint}</span>
      {chosen.length ? (
        <ol className="picker-list">
          {chosen.map((value, index) => (
            <li key={value}>
              <span>{labelOf.get(value) ?? `${value} (not on this site)`}</span>
              <button type="button" className="btn btn-small" onClick={() => move(index, -1)} disabled={index === 0} aria-label={`Move ${value} up`}>
                ↑
              </button>
              <button type="button" className="btn btn-small" onClick={() => move(index, 1)} disabled={index === chosen.length - 1} aria-label={`Move ${value} down`}>
                ↓
              </button>
              <button type="button" className="btn btn-small" onClick={() => onChange(chosen.filter((v) => v !== value))} aria-label={`Remove ${value}`}>
                ×
              </button>
            </li>
          ))}
        </ol>
      ) : (
        <span className="small">Automatic</span>
      )}
      <select value="" onChange={(e) => e.target.value && onChange([...chosen, e.target.value])} aria-label={`Add to ${label}`}>
        <option value="">Add…</option>
        {remaining.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}
